import { db } from './index.js';

/**
 * Retourne tous les magasins triés par ordre personnalisé.
 * @returns {Promise<Array>}
 */
export async function getAllStores() {
  const stores = await db.stores.toArray();
  return stores.sort((a, b) => (a.order ?? 999) - (b.order ?? 999) || a.name.localeCompare(b.name));
}

/**
 * Retourne un magasin par son id.
 * @param {number} id
 * @returns {Promise<Object|undefined>}
 */
export async function getStore(id) {
  return db.stores.get(id);
}

/**
 * Crée un nouveau magasin.
 * @param {string} name - Nom du magasin
 * @returns {Promise<number>} - Id du magasin créé
 */
export async function addStore(name) {
  const trimmedName = name?.trim();
  if (!trimmedName) {
    throw new Error('Le nom du magasin est requis');
  }

  const all = await db.stores.toArray();
  const maxOrder = all.reduce((max, s) => Math.max(max, s.order ?? -1), -1);

  return db.stores.add({
    name: trimmedName,
    order: maxOrder + 1,
    createdAt: new Date(),
  });
}

/**
 * Met à jour le nom d'un magasin.
 * @param {number} id
 * @param {string} name
 */
export async function updateStore(id, name) {
  const trimmedName = name?.trim();
  if (!trimmedName) {
    throw new Error('Le nom du magasin est requis');
  }

  const store = await db.stores.get(id);
  if (!store) {
    throw new Error('Magasin non trouvé');
  }

  await db.stores.update(id, { name: trimmedName });
}

/**
 * Supprime un magasin et toutes ses données associées (cascade).
 * @param {number} id
 */
export async function deleteStore(id) {
  const store = await db.stores.get(id);
  if (!store) {
    throw new Error('Magasin non trouvé');
  }

  await db.transaction('rw', [db.stores, db.aisles, db.articleTemplates, db.listItems], async () => {
    await db.aisles.where('storeId').equals(id).delete();
    await db.articleTemplates.where('storeId').equals(id).delete();
    await db.listItems.where('storeId').equals(id).delete();
    await db.stores.delete(id);
  });
}

/**
 * Réordonne les magasins selon un tableau d'ids.
 * @param {number[]} storeIds - Ids dans le nouvel ordre
 */
export async function reorderStores(storeIds) {
  await db.transaction('rw', db.stores, async () => {
    for (let i = 0; i < storeIds.length; i++) {
      await db.stores.update(storeIds[i], { order: i });
    }
  });
}
